import type { NativeAudienceMutationResult } from '@charitystack/schemas';
import { appendAssistantEntry, isDashboardAssistantHistoryEnabled } from '../../api/dashboardAssistantHistory';

/**
 * The source of the chat entry that records a proposal card's save outcome.
 * The assistant reads it to decide whether a list was already used (F-23).
 */
export const AUDIENCE_PROPOSAL_OUTCOME_SOURCE = 'audience-proposal-card';

/** The chat card a save came from: its thread and the proposal's id and hash. */
export type AudienceProposalOutcomeTarget = {
  threadId: string;
  proposalId: string;
  proposalHash: string;
};

/**
 * Records a proposal's save outcome in its chat thread, from the card's
 * Confirm or from the builder after Review in editor. The history append is
 * idempotent by clientEntryId: a save is keyed by the proposal, so Confirm and
 * a builder save keep one entry, and a conflict is keyed apart, so a save
 * after it is never dropped as a replay. Best effort: returns whether an
 * append was started.
 */
export function recordAudienceProposalOutcome({
  organizationId,
  target,
  result,
  source,
}: {
  organizationId: string;
  target: AudienceProposalOutcomeTarget;
  result: NativeAudienceMutationResult;
  /** Names the caller in the warning when the append fails. */
  source: string;
}) {
  if (!isDashboardAssistantHistoryEnabled()) return false;
  if (result.status !== 'saved' && result.status !== 'conflict') return false;
  // Never earlier than now: a replayed save carries its first save's time,
  // and an entry dated before the thread's newest one would move it back
  // in the history list.
  const savedAt = Date.parse(result.acknowledgement?.acknowledgedAt ?? '');
  void appendAssistantEntry({
    threadId: target.threadId,
    clientEntryId: result.status === 'saved' ? `proposal-outcome:${target.proposalId}` : `proposal-outcome:${target.proposalId}:conflict`,
    organizationId,
    role: 'action',
    kind: 'action',
    content: result.status === 'saved' ? 'Segment proposal saved.' : 'Segment proposal needs review.',
    status: 'complete',
    metadata: {
      source: AUDIENCE_PROPOSAL_OUTCOME_SOURCE,
      proposalId: target.proposalId,
      proposalHash: target.proposalHash,
      outcome: result.status,
      segmentId: result.status === 'saved' ? result.record.id : (result.acknowledgement?.targetId ?? null),
    },
    createdAt: new Date(Math.max(Number.isFinite(savedAt) ? savedAt : 0, Date.now())).toISOString(),
  }).catch((appendError) => {
    console.warn(`[${source}] Failed to record the save outcome:`, appendError);
  });
  return true;
}
